import React, { useState, useEffect } from "react";
import { Tooltip, Spin } from "antd";
import { getDocuments } from "../../../services/polly/documentService";
import { getTopics, getConversationTrend } from "../../../services/polly/classificationService";

const PREVIOUS_PERIOD = {
  this_week: 'last_week',
  this_month: 'last_month',
  this_quarter: 'last_quarter',
  this_year: 'last_year',
  last_year: null,
};

function flattenTopics(topics, result = []) {
  for (const t of topics) {
    result.push(t);
    if (t.children) flattenTopics(t.children, result);
  }
  return result;
}

function StatCard({ label, value, tooltip, primary, delta }) {
  return (
    <div className={`ci-stat-card${primary ? ' primary' : ''}`}>
      <div className="ci-stat-label">
        {label}
        {tooltip && (
          <Tooltip title={tooltip} placement="bottom">
            <span className="ci-stat-tip">?</span>
          </Tooltip>
        )}
      </div>
      <div className={`ci-stat-value${primary ? ' blue' : ''}`}>{value}</div>
      {delta && <div className={`ci-stat-delta ${delta.dir}`}>{delta.text}</div>}
    </div>
  );
}

function TopicVolumeTreemap({ topics }) {
  // Use root topics with their rolled-up counts
  const rootTopics = topics
    .filter((t) => t.conversationCount > 0)
    .sort((a, b) => b.conversationCount - a.conversationCount);

  const COLORS = ['#124173', '#1a5fa0', '#2278c0', '#f37064', '#e05f54', '#aaa', '#888', '#bbb'];

  if (rootTopics.length === 0) {
    return <div style={{ color: '#999', fontSize: 12, padding: 20, textAlign: 'center' }}>No conversation data in this period</div>;
  }

  return (
    <div className="ci-treemap">
      {rootTopics.map((t, i) => (
        <div
          key={t.id}
          className="ci-treemap-cell"
          style={{ flex: t.conversationCount, background: COLORS[i % COLORS.length] }}
          title={`${t.name}: ${t.conversationCount} conversations`}
        >
          <div className="ci-treemap-label">{t.name}</div>
          <div className="ci-treemap-count">{t.conversationCount}</div>
        </div>
      ))}
    </div>
  );
}

function LeaderboardRow({ rank, name, score, maxScore, color }) {
  const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
  return (
    <div className="ci-lb-row">
      <span className="ci-lb-rank">{rank}</span>
      <span className="ci-lb-name" title={name}>{name}</span>
      <span className="ci-lb-bar-bg">
        <span className="ci-lb-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </span>
      <span className="ci-lb-score" style={{ color }}>{score}</span>
    </div>
  );
}


function computeGapScore(topic) {
  // Gap = how much attention this topic needs
  // Combines low confidence rate with conversation volume
  const convs = topic.ownConversationCount || 0;
  if (convs === 0) return 0;
  const lowMedPct = ((topic.lowConfidencePct || 0) + (topic.medConfidencePct || 0)) / 100;
  // Scale 0-10: pure high confidence = 0, pure low confidence with volume = 10
  return Math.round(lowMedPct * 10 * 10) / 10;
}

function KnowledgeGapScatter({ topics }) {
  const leaves = flattenTopics(topics).filter((t) => t.ownConversationCount > 0);
  if (leaves.length === 0) {
    return <div className="ci-placeholder">No conversation data in this period</div>;
  }

  const maxConvs = Math.max(...leaves.map((t) => t.ownConversationCount));

  // X = journey coverage score (0-10), Y = gap score (inverted — high gap at top)
  const dots = leaves.map((t) => {
    const gap = computeGapScore(t);
    const coverage = (t.journeyCoverageScore || 0) * 10; // 0-100 scale for positioning
    const size = 8 + Math.round((t.ownConversationCount / Math.max(maxConvs, 1)) * 10);
    const color = gap >= 6 ? '#E24B4C' : gap >= 3 ? '#BA7517' : '#3B6D11';
    return { name: t.name, gap, coverage, size, color, convs: t.ownConversationCount, journeyCount: t.journeyCount || 0 };
  });

  return (
    <div className="ci-scatter-outer">
      <div className="ci-scatter-labels-top">
        <span className="ci-scatter-qlabel" style={{ color: '#E24B4C' }}>HIGHEST OPPORTUNITY</span>
        <span className="ci-scatter-qlabel" style={{ color: '#BA7517', textAlign: 'right' }}>ACTIVE COVERAGE</span>
      </div>
      <div className="ci-scatter-middle">
        <div className="ci-scatter-axis-y">Knowledge Gap</div>
        <div className="ci-scatter-canvas">
          <div className="ci-scatter-quad" style={{ top: 0, left: 0, width: '50%', height: '50%', background: 'rgba(226,75,76,0.07)' }} />
          <div className="ci-scatter-quad" style={{ top: 0, right: 0, width: '50%', height: '50%', background: 'rgba(186,117,23,0.04)' }} />
          <div className="ci-scatter-quad" style={{ bottom: 0, left: 0, width: '50%', height: '50%', background: 'rgba(0,0,0,0.02)' }} />
          <div className="ci-scatter-quad" style={{ bottom: 0, right: 0, width: '50%', height: '50%', background: 'rgba(59,109,17,0.07)' }} />
          <div className="ci-scatter-mid-h" />
          <div className="ci-scatter-mid-v" />
          {dots.map((d, i) => {
            const left = `${5 + (d.coverage / 100) * 90}%`;
            const top = `${5 + ((10 - d.gap) / 10) * 90}%`;
            return (
              <Tooltip key={i} title={`${d.name} -- Gap: ${d.gap}, Coverage: ${d.coverage / 10}/10, Journeys: ${d.journeyCount}, Convs: ${d.convs}`}>
                <div
                  className="ci-scatter-dot"
                  style={{ left, top, width: d.size, height: d.size, background: d.color }}
                />
              </Tooltip>
            );
          })}
        </div>
      </div>
      <div className="ci-scatter-labels-bottom">
        <span className="ci-scatter-qlabel" style={{ color: '#bbb' }}>LOW PRIORITY</span>
        <span className="ci-scatter-qlabel" style={{ color: '#3B6D11', textAlign: 'right' }}>WELL COVERED</span>
      </div>
      <div className="ci-scatter-axis-x">
        <span>No journey coverage</span>
        <span>Strong coverage</span>
      </div>
    </div>
  );
}

function KnowledgeGapLeaderboard({ topics }) {
  const leaves = flattenTopics(topics)
    .filter((t) => t.ownConversationCount > 0)
    .map((t) => ({ name: t.name, score: computeGapScore(t) }))
    .filter((t) => t.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);

  const maxScore = leaves.length > 0 ? Math.max(...leaves.map((t) => t.score)) : 1;

  if (leaves.length === 0) {
    return <div style={{ color: '#999', fontSize: 12 }}>No data</div>;
  }

  return (
    <div>
      {leaves.map((t, i) => {
        const color = t.score >= 6 ? '#E24B4C' : t.score >= 3 ? '#BA7517' : '#3B6D11';
        return (
          <LeaderboardRow key={i} rank={i + 1} name={t.name} score={t.score} maxScore={maxScore} color={color} />
        );
      })}
    </div>
  );
}

function computeMetrics(topicList) {
  const leaves = flattenTopics(topicList).filter((t) => t.ownConversationCount > 0);
  const totalConversations = leaves.reduce((sum, t) => sum + t.ownConversationCount, 0);
  const highConfCount = leaves.reduce(
    (sum, t) => sum + Math.round((t.highConfidencePct || 0) / 100 * t.ownConversationCount), 0);
  const highConfRate = totalConversations > 0 ? Math.round((highConfCount / totalConversations) * 100) : 0;
  return { totalConversations, highConfRate, activeTopics: leaves.length };
}

function formatDelta(current, previous, suffix = '') {
  if (previous === null || previous === undefined) return null;
  const diff = current - previous;
  if (diff === 0) return null;
  const dir = diff > 0 ? 'delta-up' : 'delta-dn';
  const arrow = diff > 0 ? '\u2191' : '\u2193';
  return { dir, text: `${arrow} ${Math.abs(diff)}${suffix} vs prior period` };
}

const TREND_COLORS = ['#124173', '#f37064', '#3B6D11', '#BA7517', '#00A3E0'];

function TrendChart({ period }) {
  const [trend, setTrend] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  useEffect(() => {
    setTrendLoading(true);
    getConversationTrend(period, 5)
      .then((res) => setTrend(res))
      .catch(() => setTrend(null))
      .finally(() => setTrendLoading(false));
  }, [period]);

  if (trendLoading) return <Spin size="small" />;
  if (!trend || !trend.weeks || trend.weeks.length === 0) {
    return <div className="ci-placeholder">No trend data in this period</div>;
  }

  const { weeks, topics: trendTopics } = trend;
  const allValues = trendTopics.flatMap((t) => weeks.map((w) => t.weekCounts[w] || 0));
  const maxVal = Math.max(...allValues, 1);

  const W = 300, H = 120, PAD_L = 30, PAD_R = 10, PAD_T = 8, PAD_B = 18;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  const px = (i) => PAD_L + (weeks.length > 1 ? (i / (weeks.length - 1)) * plotW : plotW / 2);
  const py = (v) => PAD_T + plotH - (v / maxVal) * plotH;

  // Grid lines
  const gridLines = [0, Math.round(maxVal / 2), maxVal].map((v) => {
    const y = py(v);
    return `<line x1="${PAD_L}" y1="${y}" x2="${W - PAD_R}" y2="${y}" stroke="#f0f0f0" stroke-width="1"/>` +
      `<text x="${PAD_L - 3}" y="${y + 3}" font-size="8" fill="#ccc" text-anchor="end" font-family="sans-serif">${v}</text>`;
  }).join('');

  // Week labels
  const labelIdxs = weeks.length <= 6 ? weeks.map((_, i) => i) : [0, Math.floor(weeks.length / 2), weeks.length - 1];
  const weekLabels = labelIdxs.map((i) => {
    const label = weeks[i].substring(5); // MM-DD
    return `<text x="${px(i)}" y="${H - 2}" font-size="8" fill="#ccc" text-anchor="middle" font-family="sans-serif">${label}</text>`;
  }).join('');

  // Lines + dots
  const lines = trendTopics.map((t, ti) => {
    const color = TREND_COLORS[ti % TREND_COLORS.length];
    const pts = weeks.map((w, i) => `${px(i)},${py(t.weekCounts[w] || 0)}`).join(' ');
    const dots = weeks.map((w, i) => {
      const v = t.weekCounts[w] || 0;
      return `<circle cx="${px(i)}" cy="${py(v)}" r="2" fill="${color}"/>`;
    }).join('');
    return `<polyline points="${pts}" fill="none" stroke="${color}" stroke-width="1.2" stroke-linejoin="round" stroke-linecap="round"/>${dots}`;
  }).join('');

  return (
    <div>
      <div className="ci-trend-legend">
        {trendTopics.map((t, i) => (
          <span key={t.topicId} className="ci-trend-legend-item">
            <span className="ci-trend-legend-dot" style={{ background: TREND_COLORS[i % TREND_COLORS.length] }} />
            {t.displayName}
          </span>
        ))}
      </div>
      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: 'block' }}
        dangerouslySetInnerHTML={{ __html: gridLines + weekLabels + lines }}
      />
    </div>
  );
}

export default function InsightsTab({ topics, period, loading }) {
  const [docCount, setDocCount] = useState(null);
  const [prevMetrics, setPrevMetrics] = useState(null);

  useEffect(() => {
    getDocuments({ page: 0, size: 1 })
      .then((res) => setDocCount(res.totalElements || 0))
      .catch(() => setDocCount(null));
  }, []);

  useEffect(() => {
    const prevPeriod = PREVIOUS_PERIOD[period];
    if (!prevPeriod) { setPrevMetrics(null); return; }
    getTopics(prevPeriod)
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setPrevMetrics(computeMetrics(list));
      })
      .catch(() => setPrevMetrics(null));
  }, [period]);

  const current = computeMetrics(topics);
  const { totalConversations, highConfRate, activeTopics } = current;

  return (
    <Spin spinning={loading}>
      <div className="ci-body">

        {/* Stat Cards */}
        <div className="ci-stat-row">
          <StatCard
            label="Conversations handled"
            value={totalConversations.toLocaleString()}
            tooltip="Total conversations Polly participated in during this period."
            delta={formatDelta(totalConversations, prevMetrics?.totalConversations)}
          />
          <StatCard
            label="High-confidence rate"
            value={`${highConfRate}%`}
            tooltip="% of conversations classified with confidence above the 75% threshold."
            delta={formatDelta(highConfRate, prevMetrics?.highConfRate, ' pts')}
          />
          <StatCard
            label="Vault documents"
            value={docCount !== null ? docCount : '--'}
            tooltip="Active documents in the knowledge vault."
          />
          <StatCard
            label="Active topics"
            value={activeTopics}
            tooltip="Topics with at least one conversation in this period."
            delta={formatDelta(activeTopics, prevMetrics?.activeTopics)}
          />
        </div>

        {/* Knowledge Gap Scatter + Leaderboard */}
        <div className="ci-chart-row-wide">
          <div className="ci-card">
            <div className="ci-card-title">Knowledge Gap vs. Journey Coverage</div>
            <div className="ci-card-sub">Each dot is a topic. Top-left = highest opportunity. Bottom-right = well covered</div>
            <KnowledgeGapScatter topics={topics} />
          </div>
          <div className="ci-card ci-card-narrow">
            <div className="ci-card-title">Knowledge Gap leaderboard</div>
            <div className="ci-card-sub">Topics ranked by gap score (most attention needed first)</div>
            <KnowledgeGapLeaderboard topics={topics} />
          </div>
        </div>

        {/* Topic Volume + Trend */}
        <div className="ci-chart-row">
          <div className="ci-card">
            <div className="ci-card-title">Topic volume</div>
            <div className="ci-card-sub">Conversation count by root topic this period</div>
            <TopicVolumeTreemap topics={topics} />
          </div>
          <div className="ci-card">
            <div className="ci-card-title">Conversation volume trend</div>
            <div className="ci-card-sub">Weekly count for top topics this period</div>
            <TrendChart period={period} />
          </div>
        </div>

      </div>
    </Spin>
  );
}
